/* The client's week under a missed workout — canon `zadania-c-runda5.html`'s `.week` / `.day`, kept
 * with the task card of 24 September 2026, which folded the four attendance tiles into one line.
 * The squares reserve their height before the schedule read lands, so the card does not grow when
 * it answers. */

.block {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.strip {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: var(--space-2);
}

.day {
  min-width: 0;
  height: 86px;
  padding: var(--space-10px) var(--space-6px) var(--space-2);
  text-align: center;
  background: var(--bg-inset);
  border-radius: var(--radius-13px);
}

/* A day with nothing on it. A dashed outline over the page's own ground rather than a filled box,
 * so the week reads as "four training days" at a glance. */
.dayRest {
  background: none;
  border: var(--border-hairline) dashed var(--border);
}

/* The day the TASK is about — the fact the whole card exists to point at. An INSET INK ring, which
 * is the treatment the canon gives its marked day: a coloured outline reads as a glow, and the ban
 * on one is absolute. The danger colour on this square is the glyph's alone. */
.dayTask {
  box-shadow: var(--ring-base-cell);
}

.dayName {
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* The desk's squares (direction C2 of 4 November 2026, kept in B2C): the control radius, and the
 * day and the word under it a step larger and lighter. 10px taller with even top and bottom air,
 * so the larger word under the day does not sit on the square's lower edge (owner, 5 November
 * 2026). `:where()` keeps these at one class's specificity, so today's full-ink weekday below still
 * outranks them; the quick card's compact squares keep their own look. */
.day:not(.dayCompact) {
  height: 96px;
  padding-bottom: var(--space-10px);
  border-radius: var(--r-control);
}

:where(.day:not(.dayCompact)) .dayName {
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
}

/* Today gets a QUIETER marker than the task's day, so the two do not compete for the same reading:
 * the weekday label in full ink rather than a second ring round the box. Both squares also say what
 * they are in words — see `ClientWeekStrip` — so neither is carried by a treatment alone. */
.dayToday .dayName {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* 20px on the desk: the 86px square holds the day, the mark, the session's name AND the word under
 * it (10 + 15 + 20 + 18 + 15 + 8), and at 30 the word hung 10px past the square's edge — measured,
 * 24 September 2026. The quick square draws no name, so its mark keeps the canon's 30. */
.dayMark {
  display: grid;
  place-items: center;
  height: 20px;
}

.dayMarkGlyph {
  width: 18px;
  height: 18px;
}

.markDone {
  color: var(--good-ink);
}

.markMissed {
  color: var(--danger);
}

.markPlanned {
  color: var(--text-2);
}

.dayTitle {
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.dayStatus {
  font-size: var(--fs-tag);
  color: var(--text-2);
}

/* After `.dayStatus`, at its specificity: the desk's word under the day (above). */
:where(.day:not(.dayCompact)) .dayStatus {
  font-size: var(--fs-label);
}

/* The self-paced client's one sentence, instead of seven empty squares saying something false. */
.undated {
  padding: var(--space-3) var(--space-4);
  font-size: var(--fs-label-md);
  line-height: var(--lh-normal);
  color: var(--text-2);
  background: var(--bg-inset);
  border-radius: var(--radius-13px);
}

/* The reserve. The strip's own height, held for the whole wait so the card cannot grow when the
 * schedule answers. `display: block`, because it is a `span`: an inline box ignores `height`, and
 * until 22 September 2026 this reserve held nothing at all, so the card grew by 86px when the
 * schedule landed. 96 since the desk's squares grew to 96 (owner, 5 November 2026); the quick
 * card's 74 is `.reserveCompact`. */
.reserve {
  display: block;
  height: 96px;
  border-radius: var(--radius-13px);
}

/* The quick card's squares: the day, the mark, the word — no session name. */
.dayCompact {
  height: 74px;
  padding-block-start: var(--space-6px);
}

.dayCompact .dayMark {
  height: 30px;
}

.reserveCompact {
  height: 74px;
}
